import { InjectionToken } from '@angular/core'
import { BaseParticipantInformation, IConversationTypeConfig } from '@engineering11/messaging-web'
import { RestApiClientOptions } from '@engineering11/web-api-rest'
import { IEnvironment } from './model/environment.model'
import { ErrorHandlingConfig } from './model/error-handling-config-model'

export enum COLLECTIONS {
  CANDIDATE_APPLICATION = 'jobs_candidate_application/',
  CANDIDATE_APPLICATION_STASH = 'candidate_application_stash/',
  CANDIDATE_APPLICATION_STAGE = 'jobs_candidate_application_stage/',
  CANDIDATE_APPLICATION_UPDATE_STAGE = 'jobs_candidate_application_update_stage/',
  CANDIDATE_POOL = 'candidate_pool/',
  JOB_PROFILE = 'jobs_job_profile/',
  JOB_POST_USER_CONFIG = 'jobs_job_post_user_config/',
  JOB_POST = 'jobs_job_post/',
  JOB_POST_APPLICATION = '/jobs_job_application/', // subcollection of job_post
  JOB_APPLICATION_COMMENT = '/jobs_job_application_comment/', // subcollection of job_application
  JOB_APPLICATION_REVIEW = '/jobs_job_application_review/', // subcollection of job_application
  EMPLOYER = 'employer/',
  NAV_FAVORITES = 'employer_nav_favorites/',
  REPORTED_CONTENT = 'reported_content/',
  VIRTUAL_DIALOGUE_EMPLOYER = 'virtual-dialogue_employer/',
  VIRTUAL_DIALOGUE_CANDIDATE = 'virtual-dialogue_candidate/',
  CANDIDATE_RESUME = 'candidate_resume/',
  CANDIDATE_VIDEO = 'candidate_video/',
  CANDIDATE_JOB_VM = 'candidate_job_vm/',
  JOB_APPLICATION_VIEWS = 'job_application_views/',
  JOB_POST_PROMOTION = 'jobs_job_post_promotion/',
  INTERVIEW_QUESTION = 'interview_question/',
  AUTOMATION_CONFIG = 'jobs_automation_config/',
  CUSTOMER_AUTOMATION_CONFIG = 'jobs_automation_customer_config/',
  JOBS_ASSISTANT_ANALYSIS = 'jobs_assistant_analysis/',
  // BACKEND CONTRACT (assumption) — see projects/employer/src/app/modules/assistant/BACKEND-CONTRACT.md
  // Live assistant conversation documents (Epic 4, story 4.2). Confirm path against backend assistant epics 2/3.
  ASSISTANT_CONVERSATION = 'assistant_conversations/',
  // Per-conversation run-step subcollection segment: assistant_conversations/{id}/steps/{stepId}.
  ASSISTANT_CONVERSATION_STEPS = '/steps/',
  // Top-level assistant reply/message collection the backend writes run messages to.
  ASSISTANT_MESSAGES = 'assistant_messages/',
  // BACKEND CONTRACT (assumption) — see projects/employer/src/app/modules/assistant/BACKEND-CONTRACT.md
  // Live assistant pending-action documents (Epic 4, story 4.8). Doc id = conversation id.
  // Confirm path against backend assistant epics 2/3.
  ASSISTANT_PENDING_ACTION = 'assistant_pending_actions/',
  INTEGRATION_CULTURE_INDEX_JOBS = 'integration_culture_index_jobs/',
  INTEGRATION_CULTURE_INDEX_FORM_FIELDS = 'integration_culture_index_form_fields/',
}

export enum SEARCH_INDICES {
  JOB_POST_APPLICATION = 'job_post_application',
  APPLICATION_JOB_SCORE_ASC = 'application_job_score_asc',
  APPLICATION_DATE_DESC = 'application_date_desc',
  APPLICATION_DATE_ASC = 'application_date_asc',
  APPLICATION_LAST_NAME_DESC = 'application_last_name_desc',
  APPLICATION_LAST_NAME_ASC = 'application_last_name_asc',
  APPLICATION_EXPERIENCE_DESC = 'application_experience_desc',
  APPLICATION_EXPERIENCE_ASC = 'application_experience_asc',
  STAR_RATING_ASC = 'star_rating_asc',
  STAR_RATING_DESC = 'star_rating_desc',

  JOB_PROFILE = 'job_profile',
  JOB_POST = 'job_post',

  JOB_POST_DATE_DESC = 'job_post_date_desc',
  JOB_POST_CITY_DESC = 'job_post_city_desc',
  // Ascending city replica. The index already exists in Algolia and is already listed in the
  // backend's JOB_POST_INDEX_REPLICAS (so the secured search key permits it) — it simply had no
  // web-side consumer until the campaigns table's sortable Location header (CNCT-6752).
  JOB_POST_CITY_ASC = 'job_post_city_asc',
  JOB_POST_LOCATION_DESC = 'job_post_location_desc',
  CANDIDATE_POOL = 'candidate_pool',
  CANDIDATE_POOL_LAST_NAME_ASC = 'candidate_pool_last_name_asc',
  CANDIDATE_POOL_LAST_NAME_DESC = 'candidate_pool_last_name_desc',

  // CULTURE INDEX INDICES
  CULTURE_INDEX_PATTERN_ASC = 'primary_trait_pattern_asc',
  CULTURE_INDEX_PATTERN_DESC = 'primary_trait_pattern_desc',
}

export const ENVIRONMENT = new InjectionToken<IEnvironment>('environment')
export const ERROR_HANDLING = new InjectionToken<ErrorHandlingConfig>('error_handling')

export const NEW_PROFILE_TITLE = 'New Profile'

export const NOTIFICATION_DROPDOWN_COUNT = 5
export const CONVERSATION_DROPDOWN_COUNT = 5

export const IDENTICON = {
  ROOT_PATH: 'assets/identicon/',
  TAIL: '_identity.svg',
}

// Video
export const MIN_DURATION_VIDEO = 10 // seconds
export const MAX_DURATION_VIDEO = 60 // seconds

export enum ConversationTypes {
  DirectMessage = 'direct-message',
  JobApplication = 'application-conversation',
  JobApplicationDM = 'application-conversation-dm',
  SocialDm = 'social-dm',
  CandidatePoolDM = 'candidate-pool-dm',
}

export enum InputsContext {
  Name = 'name',
  Description = 'description',
  LargeText = 'largeText',
}

export enum Sizes {
  ExtraSmall = 'xs',
  Small = 'sm',
  Medium = 'md',
  Large = 'lg',
  ExtraLarge = 'xl',
}

export const INPUTS_MAXLENGTH_BY_CONTEXT: { [key in InputsContext]: number } = {
  name: 100,
  description: 200,
  largeText: 6000,
}

// AI Summary
export const AI_SUMMARY_LIMIT = 60
export const AI_SUMMARY_WARNING_LIMIT = 5 // Warn when only 5 uses left?
export const RESUME_SUMMARY_LIMIT_KEY = 'RESUME_SUMMARY'

export const NOT_FOUND_ROUTE = '/home/404'

export enum ViewModes {
  Swimlane = 'swimlane',
  Grid = 'grid',
}

export const MessagingSystemUserIds = Object.freeze({
  hiring_team: 'hiring_team',
})

export const MessagingSystemUsers: Record<string, BaseParticipantInformation> = {
  [MessagingSystemUserIds.hiring_team]: { displayName: 'Hiring Team', profilePicture: null, userId: MessagingSystemUserIds.hiring_team },
}

export const DEFAULT_CONVERSATION_CONFIG: IConversationTypeConfig = {
  canEditOwnMessages: true,
  canDeleteOwnMessages: false,
  canReactOnMessages: true,
  canDeleteConversation: false,
  canViewAllReadReceipts: false,
  canViewOwnReadReceipts: false,
}

export const DEFAULT_NETWORK_MONITORING_CONFIG: Partial<RestApiClientOptions> = {
  enableNetworkMonitoring: true,
  networkMonitoringConfig: {
    minRequestsForAnalysis: 3,
    slowRequestThreshold: 2000,
    averageResponseTimeThreshold: 1500,
    windowSize: 120000,
  },
}
